export default function CaseStudies() {
  const caseStudies = [
    {
      title: 'Aviation Cardiac Emergency Response',
      client: 'Global Executive Aviation',
      challenge: 'Private aviation fleet needed comprehensive medical training for crews operating long-haul international flights with UHNW passengers.',
      solution: 'Implemented intensive 2-day aviation medical training program covering cardiac emergencies, trauma response, and high-altitude medical protocols.',
      outcome: 'Within 2 days of training completion, crew successfully managed a passenger cardiac arrest at 41,000 feet, coordinating with ground medical support and executing emergency landing protocols. Passenger survived with full recovery.',
      image: 'https://public.readdy.ai/ai/img_res/af2ac253f54ee21bb65406bc9337803a.jpg',
      metrics: [
        { label: 'Crew Members Trained', value: '24' },
        { label: 'Training Duration', value: '2 Days' },
        { label: 'Lives Saved', value: '1' },
        { label: 'Response Time', value: '< 30 Seconds' }
      ]
    },
    {
      title: 'Maritime Offshore Trauma Management',
      client: 'Mediterranean Yacht Operations',
      challenge: 'Superyacht crew needed advanced medical training for extended offshore operations where evacuation could take hours.',
      solution: 'Developed custom maritime medical program focusing on trauma management, extended care protocols, and helicopter evacuation coordination.',
      outcome: 'Crew successfully stabilized a case of fainting due to hypoglycemia 200 nautical miles offshore, coordinated helicopter evacuation, and maintained patient care during 2-hour evacuation. Crew member made full recovery.',
      image: 'https://readdy.ai/api/search-image?query=luxury-superyacht-deck-with-helicopter-landing-pad-medical-emergency-equipment-professional-yacht-crew-in-white-uniforms-crystal-clear-ocean-background-sophisticated-maritime-medical-preparedness-bright-daylight&width=800&height=600&seq=casestudy002&orientation=landscape',
      metrics: [
        { label: 'Crew Members Trained', value: '18' },
        { label: 'Training Duration', value: '2 Days' },
        { label: 'Evacuation Time', value: '2 Hours' },
        { label: 'Patient Outcome', value: 'Full Recovery' }
      ]
    },
    {
      title: 'Estate Medical Security Integration',
      client: 'Belmont Estate',
      challenge: 'Large private estate with 30+ staff needed discreet medical emergency capabilities integrated with existing security protocols.',
      solution: 'Implemented tiered training program for all estate staff, with advanced certification for security team and household managers.',
      outcome: 'Head housekeeper used AED training to save guest experiencing cardiac arrest. Coordinated response with estate security and emergency services resulted in successful resuscitation and full recovery.',
      image: 'https://readdy.ai/api/search-image?query=elegant-luxury-private-estate-interior-with-discreet-medical-emergency-equipment-professional-estate-staff-sophisticated-residential-setting-high-end-household-medical-preparedness-natural-lighting&width=800&height=600&seq=casestudy003&orientation=landscape',
      metrics: [
        { label: 'Staff Members Trained', value: '32' },
        { label: 'Training Duration', value: '3 Days' },
        { label: 'Response Time', value: '< 2 Minutes' },
        { label: 'Lives Saved', value: '1' }
      ]
    }
  ];

  return (
    <section className="bg-[#0D0D0D] py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <div className="text-[9px] uppercase tracking-[0.2em] text-[#707070] font-light mb-4">
            Real-World Impact
          </div>
          <h2 className="text-[40px] sm:text-[48px] md:text-[56px] font-bold text-[#F5F5F5] mb-6 font-serif">
            Case Studies
          </h2>
          <p className="text-[16px] text-[#9A9A9A] max-w-[800px] mx-auto leading-relaxed">
            Real emergencies. Real responses. Real lives saved. These case studies demonstrate the critical importance of professional medical training.
          </p>
        </div>

        <div className="space-y-16">
          {caseStudies.map((study, index) => (
            <div key={index} className="bg-[#0F0F0F] border border-[#1A1A1A] rounded-2xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative h-[400px] lg:h-auto">
                  <img 
                    src={study.image}
                    alt={study.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent lg:bg-gradient-to-r"></div>
                  <div className="absolute top-6 left-6 bg-[#D4C4B0] text-black px-4 py-2 rounded-lg text-[12px] font-semibold uppercase tracking-wider">
                    Case Study {index + 1}
                  </div>
                </div>

                <div className="p-8 md:p-12">
                  <h3 className="text-[28px] md:text-[32px] font-bold text-[#F5F5F5] mb-3 font-serif">
                    {study.title}
                  </h3>
                  <p className="text-[14px] text-[#D4C4B0] mb-8 uppercase tracking-wider">
                    {study.client}
                  </p>

                  <div className="space-y-6 mb-8">
                    <div>
                      <h4 className="text-[14px] font-semibold text-[#C0C0C0] mb-2 uppercase tracking-wider">
                        Challenge
                      </h4>
                      <p className="text-[15px] text-[#A0A0A0] leading-relaxed">
                        {study.challenge}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-[14px] font-semibold text-[#C0C0C0] mb-2 uppercase tracking-wider">
                        Solution
                      </h4>
                      <p className="text-[15px] text-[#A0A0A0] leading-relaxed">
                        {study.solution}
                      </p>
                    </div>

                    <div>
                      <h4 className="text-[14px] font-semibold text-[#C0C0C0] mb-2 uppercase tracking-wider">
                        Outcome
                      </h4>
                      <p className="text-[15px] text-[#A0A0A0] leading-relaxed">
                        {study.outcome}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {study.metrics.map((metric, metricIndex) => (
                      <div key={metricIndex} className="bg-[#111111] border border-[#222222] rounded-xl p-4">
                        <div className="text-[24px] font-bold text-[#D4C4B0] mb-1">
                          {metric.value}
                        </div>
                        <div className="text-[12px] text-[#808080] uppercase tracking-wider">
                          {metric.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 bg-[#111111] border border-[#222222] rounded-2xl p-12 text-center">
          <i className="ri-shield-check-line text-[64px] text-[#D4C4B0] mb-6"></i>
          <h3 className="text-[32px] font-bold text-[#F5F5F5] mb-4 font-serif">
            Your Success Story Starts Here
          </h3>
          <p className="text-[16px] text-[#9A9A9A] max-w-[700px] mx-auto mb-8 leading-relaxed">
            Every case study represents lives saved through proper training and preparation. Let us help you build the same level of medical readiness for your team.
          </p>
          <a 
            href="#contact" 
            className="inline-block px-8 py-4 bg-[#D4C4B0] text-black text-[14px] font-semibold rounded-lg hover:bg-[#C4B4A0] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Start Your Training Program
          </a>
        </div>
      </div>
    </section>
  );
}
